import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  ArrowPathIcon,
  BookOpenIcon, 
  PencilIcon,
  TrashIcon,
  BriefcaseIcon,
  VideoCameraIcon,
  LinkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  CalendarDaysIcon,
  ClockIcon,
  EnvelopeIcon,
  SignalIcon,
  SignalSlashIcon,
  ShieldCheckIcon,
  InformationCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import api from '../utils/api';
import { toast } from '../utils/notification';

// Helper for apiRequest using the configured axios instance
const apiRequest = async (url, method = 'GET', data = null) => {
  try {
    const config = { method, url };
    if (data) config.data = data;
    const response = await api(config);
    return response.data;
  } catch (err) {
    throw err.response?.data || err;
  }
};

const Diary = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNewEntryForm, setShowNewEntryForm] = useState(false);
  const [showConsultationForm, setShowConsultationForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    date: new Date().toISOString().split('T')[0],
    content: '',
    case: '',
    tags: ''
  });
  const [consultationData, setConsultationData] = useState({
    summary: 'Legal Consultation — EasyLaw',
    description: '',
    client_email: '',
    start_date: new Date().toISOString().split('T')[0],
    start_time: '09:00',
    end_date: new Date().toISOString().split('T')[0],
    end_time: '10:00',
    timezone: 'Africa/Harare',
    case_id: '',
    is_billable: false,
    billing_rate: ''
  });
  const [cases, setCases] = useState([]);
  const [googleStatus, setGoogleStatus] = useState({ connected: false, loading: true });
  const [consultationLoading, setConsultationLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('entries'); // 'entries' | 'consultations'
  const [showScopeConsent, setShowScopeConsent] = useState(false);

  useEffect(() => {
    fetchEntries();
    fetchCases();
    checkGoogleStatus();

    // Handle Google Calendar OAuth redirect result
    const params = new URLSearchParams(window.location.search);
    const gcalResult = params.get('gcal');
    if (gcalResult === 'success') {
      toast.success('Google Calendar connected successfully!');
      // Remove query params from URL without reload
      window.history.replaceState({}, '', window.location.pathname);
    } else if (gcalResult === 'error') {
      const reason = params.get('reason') || 'unknown';
      toast.error(`Failed to connect Google Calendar (${reason}). Please try again.`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const checkGoogleStatus = async () => {
    try {
      setGoogleStatus(prev => ({ ...prev, loading: true }));
      const response = await apiRequest('/calendar/google/status');
      setGoogleStatus({ 
        connected: response.connected || false, 
        loading: false,
        scopes: response.scopes,
        token_expiry: response.token_expiry,
        updated_at: response.updated_at
      });
    } catch (err) {
      console.error('Failed to check Google Calendar status:', err);
      setGoogleStatus({ connected: false, loading: false });
    }
  };

  const handleGoogleConnect = async () => {
    // Show pre-consent explanation before redirecting to Google
    setShowScopeConsent(true);
  };

  const proceedToGoogleConsent = async () => {
    setShowScopeConsent(false);
    try {
      const response = await apiRequest('/calendar/google/connect');
      if (response.authorization_url) {
        // Redirect in current window — the OAuth callback will redirect back to /diary
        window.location.href = response.authorization_url;
      }
    } catch (err) {
      console.error('Failed to initiate Google Calendar connection:', err);
      toast.error('Failed to connect Google Calendar. Please try again.');
    }
  };

  const handleGoogleDisconnect = async () => {
    if (!window.confirm('Disconnect your Google Calendar? Existing Meet links will still work.')) return;
    try {
      await apiRequest('/calendar/google/disconnect', 'POST');
      setGoogleStatus({ connected: false, loading: false });
      toast.success('Google Calendar disconnected.');
    } catch (err) {
      console.error('Failed to disconnect Google Calendar:', err);
      toast.error('Failed to disconnect Google Calendar.');
    }
  };

  const fetchEntries = async () => {
    try {
      setLoading(true);
      // We'll reuse the calendar API endpoints but adapt the UI to a diary format
      const response = await apiRequest('/calendar/events');
      
      // Error responses might be objects with error details
      if (!response || typeof response !== 'object' || response.detail) {
        throw new Error(response?.detail || 'Failed to fetch diary entries');
      }
      
      // Ensure we have an array of events
      const events = Array.isArray(response) 
        ? response 
        : Array.isArray(response?.events) 
          ? response.events 
          : [];
      
      // Transform calendar events into diary entries
      const transformedData = events.map(event => ({
        id: event.id,
        title: event.title,
        date: event.start_time.split('T')[0],
        content: event.description || '',
        case: event.case,
        case_details: event.case_details,
        tags: event.event_type,
        created_at: event.created_at,
        updated_at: event.updated_at,
        meet_link: event.meet_link || event.location || null,
        html_link: event.html_link || null,
        google_event_id: event.google_event_id || null,
        client_email: event.client_email || null,
        start_time: event.start_time,
        end_time: event.end_time,
        location: event.location || null
      }));
      
      setEntries(transformedData);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch diary entries:', err);
      // If we have an error message from the API, show that instead of the generic message
      setError(err.message || 'Failed to load diary entries. Please try again.');
      setEntries([]); // Ensure entries is always an array
    } finally {
      setLoading(false);
    }
  };

  const fetchCases = async () => {
    try {
      const response = await apiRequest('/api/cases/');
      // Handle different response formats - could be response.data or response directly
      setCases(Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : []);
    } catch (err) {
      console.error('Failed to fetch cases:', err);
      setCases([]); // Ensure cases is always an array on error
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Transform diary entry to calendar event format for API
      const payload = {
        title: formData.title,
        description: formData.content,
        event_type: formData.tags || 'other',
        start_time: `${formData.date}T00:00:00`,
        end_time: `${formData.date}T23:59:59`,
        all_day: true,
        case: formData.case || null
      };
      
      if (editingEntry) {
        await apiRequest(`/calendar/events/${editingEntry.id}`, 'PUT', payload);
        toast.success('Diary entry updated successfully');
      } else {
        await apiRequest('/calendar/events', 'POST', payload);
        toast.success('Diary entry created successfully');
      }
      
      // Reset form and refresh entries
      setFormData({
        title: '',
        date: new Date().toISOString().split('T')[0],
        content: '',
        case: '',
        tags: ''
      });
      
      setShowNewEntryForm(false);
      setEditingEntry(null);
      fetchEntries();
    } catch (err) {
      console.error('Error saving diary entry:', err);
      toast.error('Failed to save diary entry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      title: entry.title,
      date: entry.date,
      content: entry.content,
      case: entry.case,
      tags: entry.tags
    });
    setShowNewEntryForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this diary entry?')) return;
    
    try {
      await apiRequest(`/calendar/events/${id}`, 'DELETE');
      setEntries(entries.filter(entry => entry.id !== id));
      toast.success('Diary entry deleted successfully');
    } catch (err) {
      console.error('Error deleting diary entry:', err);
      toast.error('Failed to delete diary entry');
    }
  };

  const handleConsultationChange = (e) => {
    const { name, value, type, checked } = e.target;
    setConsultationData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleConsultationSubmit = async (e) => {
    e.preventDefault();
    
    if (!googleStatus.connected) {
      toast.error('Please connect your Google Calendar first.');
      return;
    }

    try {
      setConsultationLoading(true);
      
      const startDateTime = `${consultationData.start_date}T${consultationData.start_time}:00`;
      const endDateTime = `${consultationData.end_date}T${consultationData.end_time}:00`;

      const payload = {
        summary: consultationData.summary,
        description: consultationData.description,
        client_email: consultationData.client_email,
        start_time: startDateTime,
        end_time: endDateTime,
        timezone: consultationData.timezone,
        case_id: consultationData.case_id ? parseInt(consultationData.case_id) : null,
        is_billable: consultationData.is_billable,
        billing_rate: consultationData.billing_rate ? parseFloat(consultationData.billing_rate) : null
      };

      const response = await apiRequest('/calendar/events/consultation', 'POST', payload);
      
      const meetMsg = response.meet_link
        ? `Consultation scheduled! Meet link: ${response.meet_link}`
        : 'Consultation scheduled!';
      toast.success(meetMsg);

      // Reset form
      setConsultationData({
        summary: 'Legal Consultation — EasyLaw',
        description: '',
        client_email: '',
        start_date: new Date().toISOString().split('T')[0],
        start_time: '09:00',
        end_date: new Date().toISOString().split('T')[0],
        end_time: '10:00',
        timezone: 'Africa/Harare',
        case_id: '',
        is_billable: false,
        billing_rate: ''
      });
      setShowConsultationForm(false);
      
      // Refresh entries to show new consultation
      fetchEntries();
    } catch (err) {
      console.error('Error creating consultation:', err);
      const detail = typeof err?.detail === 'string' 
        ? err.detail 
        : 'Failed to create consultation. Please try again.';
      toast.error(detail);
    } finally {
      setConsultationLoading(false);
    }
  };

  const formatTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    const date = new Date(dateTimeString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const isMeetLink = (url) => {
    return url && (url.includes('meet.google.com') || url.includes('hangouts'));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }).format(date);
  };

  if (loading && entries.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Google OAuth Pre-Consent Modal */}
      {showScopeConsent && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="scope-consent-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowScopeConsent(false)} />
            <div className="relative inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-6 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button onClick={() => setShowScopeConsent(false)} className="text-gray-400 hover:text-gray-500">
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/40 sm:mx-0 sm:h-10 sm:w-10">
                  <ShieldCheckIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="scope-consent-title">
                    Connect Google Calendar
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      EasyLaw needs your permission to access your Google Calendar to provide the following features:
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-4 space-y-3">
                <div className="rounded-md bg-gray-50 dark:bg-gray-700/50 p-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Requested Permission:
                  </h4>
                  <div className="flex items-start space-x-3">
                    <CalendarDaysIcon className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        Google Calendar Events
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Scope: calendar.events
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-md bg-blue-50 dark:bg-blue-900/20 p-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                    <InformationCircleIcon className="h-4 w-4 mr-1 text-blue-500" />
                    How EasyLaw uses this permission:
                  </h4>
                  <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span><strong>Create consultation events</strong> on your calendar with scheduled times</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span><strong>Generate Google Meet links</strong> automatically for client video calls</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span><strong>Send calendar invites</strong> to clients with meeting details</span>
                    </li>
                  </ul>
                </div>

                <div className="rounded-md bg-green-50 dark:bg-green-900/20 p-3">
                  <p className="text-xs text-green-800 dark:text-green-200 flex items-start">
                    <ShieldCheckIcon className="h-4 w-4 mr-1.5 flex-shrink-0 mt-0" />
                    <span>EasyLaw only accesses calendar events. We do not read your emails, contacts, or any other Google data. You can revoke access at any time.</span>
                  </p>
                </div>
              </div>

              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={proceedToGoogleConsent}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Continue to Google Sign-In
                </button>
                <button
                  type="button"
                  onClick={() => setShowScopeConsent(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-700 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">My Legal Diary</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Keep track of your case notes, consultations, and meetings</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={fetchEntries}
            className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            <ArrowPathIcon className="h-4 w-4 mr-1" />
            Refresh
          </button>
          <button
            onClick={() => {
              setEditingEntry(null);
              setFormData({
                title: '',
                date: new Date().toISOString().split('T')[0],
                content: '',
                case: '',
                tags: ''
              });
              setShowNewEntryForm(!showNewEntryForm);
              setShowConsultationForm(false);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            New Entry
          </button>
          <button
            onClick={() => {
              setShowConsultationForm(!showConsultationForm);
              setShowNewEntryForm(false);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700"
          >
            <VideoCameraIcon className="h-4 w-4 mr-1" />
            Schedule Consultation
          </button>
        </div>
      </div>

      {/* Google Calendar Connection Banner */}
      <div className={`rounded-lg border p-4 ${
        googleStatus.loading 
          ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
          : googleStatus.connected 
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
            : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {googleStatus.loading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-gray-400"></div>
            ) : googleStatus.connected ? (
              <CheckCircleIcon className="h-6 w-6 text-green-500" />
            ) : (
              <ExclamationCircleIcon className="h-6 w-6 text-yellow-500" />
            )}
            <div>
              <p className={`text-sm font-medium ${
                googleStatus.connected 
                  ? 'text-green-800 dark:text-green-200' 
                  : 'text-yellow-800 dark:text-yellow-200'
              }`}>
                {googleStatus.loading 
                  ? 'Checking Google Calendar connection...'
                  : googleStatus.connected 
                    ? 'Google Calendar Connected'
                    : 'Google Calendar Not Connected'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {googleStatus.connected 
                  ? 'You can schedule consultations with automatic Google Meet links.'
                  : 'Connect to create consultations with Google Meet video links.'}
              </p>
            </div>
          </div>
          {!googleStatus.loading && (
            <button
              onClick={googleStatus.connected ? handleGoogleDisconnect : handleGoogleConnect}
              className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                googleStatus.connected
                  ? 'border-red-300 text-red-700 hover:bg-red-100 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/30'
                  : 'border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-600 dark:text-blue-300 dark:hover:bg-blue-900/30'
              }`}
            >
              {googleStatus.connected ? (
                <>
                  <SignalSlashIcon className="h-3.5 w-3.5 mr-1" />
                  Disconnect
                </>
              ) : (
                <>
                  <SignalIcon className="h-3.5 w-3.5 mr-1" />
                  Connect Google Calendar
                </>
              )}
            </button>
          )}
        </div>
        {/* Connection details — visible when connected */}
        {googleStatus.connected && (
          <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-800">
            <div className="flex items-center space-x-4 text-xs text-green-700 dark:text-green-300">
              <span className="flex items-center">
                <ShieldCheckIcon className="h-3.5 w-3.5 mr-1" />
                Calendar access is active and secure
              </span>
              <span className="flex items-center">
                <ClockIcon className="h-3.5 w-3.5 mr-1" />
                Stays connected automatically
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Consultation Scheduling Form */}
      {showConsultationForm && (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border border-green-200 dark:border-green-800">
          <div className="flex items-center space-x-2 mb-4">
            <VideoCameraIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              Schedule Consultation with Google Meet
            </h2>
          </div>
          
          {!googleStatus.connected && (
            <div className="mb-4 rounded-md bg-yellow-50 dark:bg-yellow-900/20 p-3">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                You need to connect your Google Calendar first to schedule consultations with Meet links.
              </p>
            </div>
          )}

          <form onSubmit={handleConsultationSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="md:col-span-2">
                <label htmlFor="summary" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Meeting Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="summary"
                  name="summary"
                  value={consultationData.summary}
                  onChange={handleConsultationChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="client_email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  <span className="inline-flex items-center">
                    <EnvelopeIcon className="h-3.5 w-3.5 mr-1" />
                    Client Email <span className="text-red-500 ml-1">*</span>
                  </span>
                </label>
                <input
                  type="email"
                  id="client_email"
                  name="client_email"
                  value={consultationData.client_email}
                  onChange={handleConsultationChange}
                  required
                  placeholder="client@example.com"
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="consultation_case" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Related Case
                </label>
                <select
                  id="consultation_case"
                  name="case_id"
                  value={consultationData.case_id}
                  onChange={handleConsultationChange}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                >
                  <option value="">None</option>
                  {Array.isArray(cases) && cases.map(c => (
                    <option key={c.id} value={c.id}>{c.title}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label htmlFor="start_date" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  <span className="inline-flex items-center">
                    <CalendarDaysIcon className="h-3.5 w-3.5 mr-1" />
                    Start Date
                  </span>
                </label>
                <input
                  type="date"
                  id="start_date"
                  name="start_date"
                  value={consultationData.start_date}
                  onChange={handleConsultationChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="start_time" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  <span className="inline-flex items-center">
                    <ClockIcon className="h-3.5 w-3.5 mr-1" />
                    Start Time
                  </span>
                </label>
                <input
                  type="time"
                  id="start_time"
                  name="start_time"
                  value={consultationData.start_time}
                  onChange={handleConsultationChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="end_date" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  End Date
                </label>
                <input
                  type="date"
                  id="end_date"
                  name="end_date"
                  value={consultationData.end_date}
                  onChange={handleConsultationChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="end_time" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  End Time
                </label>
                <input
                  type="time"
                  id="end_time"
                  name="end_time"
                  value={consultationData.end_time}
                  onChange={handleConsultationChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="consultation_description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Agenda / Description
              </label>
              <textarea
                id="consultation_description"
                name="description"
                rows="3"
                value={consultationData.description}
                onChange={handleConsultationChange}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                placeholder="Add an agenda or notes for this consultation..."
              ></textarea>
            </div>

            <div className="mb-4 p-4 rounded-md bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_billable"
                    name="is_billable"
                    checked={consultationData.is_billable}
                    onChange={handleConsultationChange}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_billable" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Billable Meeting
                  </label>
                </div>
                {consultationData.is_billable && (
                  <div className="flex items-center space-x-2">
                    <label htmlFor="billing_rate" className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      Rate ($/hr)
                    </label>
                    <input
                      type="number"
                      id="billing_rate"
                      name="billing_rate"
                      value={consultationData.billing_rate}
                      onChange={handleConsultationChange}
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      className="w-28 rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                )}
              </div>
              {consultationData.is_billable && (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  This consultation will be tracked as billable time and can be added to an invoice.
                </p>
              )}
            </div>

            <div className="flex justify-between items-center">
              <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                <VideoCameraIcon className="h-3.5 w-3.5 mr-1 text-green-500" />
                A Google Meet link will be auto-generated and sent to the client.
              </p>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowConsultationForm(false)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={consultationLoading || !googleStatus.connected}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {consultationLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      Scheduling...
                    </>
                  ) : (
                    <>
                      <VideoCameraIcon className="h-4 w-4 mr-1" />
                      Schedule with Meet
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Diary Entry Form */}
      {showNewEntryForm && (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow mb-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            {editingEntry ? 'Edit Diary Entry' : 'New Diary Entry'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleFormChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="date" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleFormChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
            
            <div className="mb-4">
              <label htmlFor="content" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Entry Content <span className="text-red-500">*</span>
              </label>
              <textarea
                id="content"
                name="content"
                rows="6"
                value={formData.content}
                onChange={handleFormChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                placeholder="Write your diary entry here..."
              ></textarea>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="case" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Related Case
                </label>
                <select
                  id="case"
                  name="case"
                  value={formData.case}
                  onChange={handleFormChange}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                >
                  <option value="">None</option>
                  {Array.isArray(cases) && cases.map(c => (
                    <option key={c.id} value={c.id}>{c.title}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="tags" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Entry Type
                </label>
                <select
                  id="tags"
                  name="tags"
                  value={formData.tags}
                  onChange={handleFormChange}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                >
                  <option value="meeting">Meeting Notes</option>
                  <option value="consultation">Consultation</option>
                  <option value="research">Legal Research</option>
                  <option value="client">Client Communication</option>
                  <option value="court">Court Proceedings</option>
                  <option value="strategy">Case Strategy</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowNewEntryForm(false)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                {editingEntry ? 'Update Entry' : 'Save Entry'}
              </button>
            </div>
          </form>
        </div>
      )}

      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('entries')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'entries'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400'
            }`}
          >
            <BookOpenIcon className="h-4 w-4 inline mr-1" />
            All Entries ({entries.length})
          </button>
          <button
            onClick={() => setActiveTab('consultations')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'consultations'
                ? 'border-green-500 text-green-600 dark:text-green-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400'
            }`}
          >
            <VideoCameraIcon className="h-4 w-4 inline mr-1" />
            Consultations ({entries.filter(e => isMeetLink(e.meet_link) || e.tags === 'consultation').length})
          </button>
        </nav>
      </div>

      {/* Entries List */}
      {(() => {
        const filteredEntries = activeTab === 'consultations'
          ? entries.filter(e => isMeetLink(e.meet_link) || e.tags === 'consultation')
          : entries;

        if (filteredEntries.length === 0) {
          return (
            <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow">
              {activeTab === 'consultations' ? (
                <>
                  <VideoCameraIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No consultations yet</h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Schedule a consultation with a Google Meet link to get started.
                  </p>
                  <div className="mt-6">
                    <button
                      onClick={() => {
                        setShowConsultationForm(true);
                        setShowNewEntryForm(false);
                      }}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                    >
                      <VideoCameraIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                      Schedule Consultation
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <BookOpenIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No diary entries yet</h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Start documenting your legal work by creating a new diary entry.
                  </p>
                  <div className="mt-6 flex justify-center space-x-3">
                    <button
                      onClick={() => {
                        setEditingEntry(null);
                        setFormData({
                          title: '',
                          date: new Date().toISOString().split('T')[0],
                          content: '',
                          case: '',
                          tags: ''
                        });
                        setShowNewEntryForm(true);
                      }}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                    >
                      <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                      New Diary Entry
                    </button>
                    <button
                      onClick={() => {
                        setShowConsultationForm(true);
                        setShowNewEntryForm(false);
                      }}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                    >
                      <VideoCameraIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                      Schedule Consultation
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        }

        return (
          <div className="space-y-6">
            {filteredEntries.map(entry => (
              <div key={entry.id} className={`bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden ${
                isMeetLink(entry.meet_link) ? 'ring-1 ring-green-200 dark:ring-green-800' : ''
              }`}>
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    {isMeetLink(entry.meet_link) && (
                      <span className="flex-shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-full bg-green-100 dark:bg-green-900/40">
                        <VideoCameraIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
                      </span>
                    )}
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{entry.title}</h2>
                      <div className="flex items-center space-x-3 text-sm text-gray-500 dark:text-gray-400">
                        <span>{formatDate(entry.date)}</span>
                        {entry.start_time && !entry.start_time.includes('00:00:00') && (
                          <span className="flex items-center">
                            <ClockIcon className="h-3.5 w-3.5 mr-0.5" />
                            {formatTime(entry.start_time)} - {formatTime(entry.end_time)}
                          </span>
                        )}
                        {entry.client_email && (
                          <span className="flex items-center">
                            <EnvelopeIcon className="h-3.5 w-3.5 mr-0.5" />
                            {entry.client_email}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {/* Google Meet Join Button */}
                    {isMeetLink(entry.meet_link) && (
                      <a
                        href={entry.meet_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 shadow-sm transition-colors"
                      >
                        <VideoCameraIcon className="h-3.5 w-3.5 mr-1" />
                        Join Meet
                      </a>
                    )}
                    {/* Google Calendar Link */}
                    {entry.html_link && (
                      <a
                        href={entry.html_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-blue-500 dark:hover:text-blue-400"
                        title="View in Google Calendar"
                      >
                        <LinkIcon className="h-5 w-5" />
                      </a>
                    )}
                    <button
                      onClick={() => handleEdit(entry)}
                      className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                      data-testid={`edit-entry-${entry.id}`}
                      aria-label="Edit entry"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(entry.id)}
                      className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                      data-testid={`delete-entry-${entry.id}`}
                      aria-label="Delete entry"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <div className="p-6">
                  {entry.content && (
                    <div className="prose dark:prose-invert max-w-none">
                      {entry.content.split('\n').map((paragraph, i) => (
                        <p key={i}>{paragraph}</p>
                      ))}
                    </div>
                  )}

                  {/* Meet Link Display */}
                  {isMeetLink(entry.meet_link) && (
                    <div className="mt-4 flex items-center p-3 rounded-md bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                      <VideoCameraIcon className="h-5 w-5 text-green-600 dark:text-green-400 mr-2 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-green-800 dark:text-green-200">Google Meet</p>
                        <a
                          href={entry.meet_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-green-600 dark:text-green-400 hover:underline truncate block"
                        >
                          {entry.meet_link}
                        </a>
                      </div>
                      <a
                        href={entry.meet_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 ml-3"
                      >
                        Join
                      </a>
                    </div>
                  )}
                  
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    {entry.case && entry.case_details && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                        <BriefcaseIcon className="h-3 w-3 mr-1" />
                        {entry.case_details.title}
                      </span>
                    )}
                    {entry.tags && (
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        entry.tags === 'consultation'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                      }`}>
                        {entry.tags === 'consultation' && <VideoCameraIcon className="h-3 w-3 mr-1" />}
                        {entry.tags}
                      </span>
                    )}
                    {entry.google_event_id && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                        <CalendarDaysIcon className="h-3 w-3 mr-1" />
                        Google Calendar
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      })()}
    </div>
  );
};

export default Diary;
